import { Edit } from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import SendbirdChat from "@sendbird/chat";
import { GroupChannelModule } from "@sendbird/chat/groupChannel";
import Availabilities from "app/components/Business/Availabilities";
import CollapsibleText from "app/components/CollapsibleText";
import CustomerModal from "app/components/CustomerModal";
import EditBusinessProfile from "app/components/EditBusiness/EditBusinessProfile";
import ShareBusinessModal from "app/components/Home/ShareBusinessModal";
import Layout from "app/components/Layout";
import { PageContainer } from "app/components/PageContainer";
import ReferreLogo from "assets/icons/Referee_B_Logo.svg";
import BusinessImage from "assets/images/business.png";
import dayjs, { Dayjs } from "dayjs";
import { awsImageRootUrl } from "lib/constants";
import { useGetBusinessesByIdQuery, useGetBusinessesByUserIdQuery } from "lib/features/business/businessService";
import {
    useLazyGetMyItemsAvailabilitiesQuery
} from "lib/features/item/itemService";
import { User } from "lib/features/user/types";
import { selectUser } from "lib/features/user/userSlice";
import { useAppSelector } from "lib/hooks";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import authServices from "services/auth-service";
import chatServices from "services/chat-service";
import { businessProfileStyles } from "styles/BusinessDirectory/businessProfile.styles";
// import { formatAddress } from "util/formatAddress";
import { parseImageUrl } from "util/parseImageUrl";
import { APP_ID } from "util/sendbirdHelper";
import MessageIcon from "../../../assets/icons/light_message.svg";
import ListIcon from "../../../assets/icons/list.svg";
import LocationIcon from "../../../assets/icons/location_green_icon.svg";

const formatAddress = (address1: string | undefined, city: string | undefined, state: string | undefined) => {
  const parts = [
    address1,
    city && state ? `${city}, ${state}` : city || state
  ].filter(Boolean);
  
  return parts.join(', ');
};

type BusinessProfilePageProps = {
  user: User;
  showDetails?: string;
};

const BusinessProfilePage = ({
  user,
  showDetails,
}: BusinessProfilePageProps) => {
  const { id } = useParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const dateParam = queryParams.get("date");
  const referredByParam = Number(queryParams.get("referredBy"));
  const showBack = queryParams.get("showBack");

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoadingAvailabilities, setIsLoadingAvailabilities] =
    useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [date, setDate] = useState<Dayjs>(
    dateParam ? dayjs(dateParam) : dayjs()
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [userData, setUserData] = useState<{
    name: string;
    avatar?: string;
    isBusiness?: boolean;
  } | null>(null);

  const {
    data: businessDetails,
    isLoading: isLoadingBusiness,
    refetch,
  } = useGetBusinessesByIdQuery({ id: Number(id) }, {});
  const {
    data: referredByBusinessData,
    isLoading: isLoadingReferredByBusiness,
  } = useGetBusinessesByUserIdQuery(
    { id: referredByParam },
    { skip: !referredByParam }
  );

  const [getMyItemsAvailabilities, { data: availabilitiesData }] =
    useLazyGetMyItemsAvailabilitiesQuery();

  const initializeSendbird = async () => {
    try {
      if (user?.id) {
        const sb = await SendbirdChat.init({
          appId: APP_ID,
          modules: [new GroupChannelModule()],
        });

        await sb.connect(user?.id?.toString());
        return sb;
      }
    } catch (error) {
      console.log("Error initializing Sendbird: ", error);
    }
  };

  const handleBusinessShare = async () => {
    setLink(`https://app.refereree.com/business/${id}?referredBy=${user?.id}`);
    setIsShareModalOpen(true);
  };

  const onPressCreateChannel = async () => {
    let channelUrl = "";

    if (loading) return;

    if (user?.id && businessDetails?.business_users[0]?.id) {
      setLoading(true);
      const response = await chatServices.getChannelUrl(
        businessDetails?.business_users[0]?.id,
        user?.id
      );
      channelUrl = response?.data?.data?.channel;
      if (channelUrl) {
        setLoading(false);
        navigate(`/chat/${channelUrl}`);
        return;
      } else {
        const sb = await initializeSendbird();

        if (sb) {
          try {
            const params = {
              invitedUserIds: [
                user?.id?.toString(),
                businessDetails?.business_users[0]?.id?.toString(),
              ],
              operatorUserIds: [
                user?.id?.toString(),
                businessDetails?.business_users[0]?.id?.toString(),
              ],
            };

            const channel = await sb.groupChannel.createChannel(params);

            let body = {
              users: [
                user?.id?.toString(),
                businessDetails?.business_users[0]?.id?.toString(),
              ],
              channel: channel?.url,
            };

            chatServices
              .createChat(body)
              .then((response) => {
                setLoading(false);
                navigate(`/chat/${response?.data?.data?.channel}`);
              })
              .catch((error) => {
                setLoading(false);
                console.log("error occured: ", error);
              });
          } catch (error) {
            setLoading(false);
            console.log("Failed to create channel", error);
          }
        }
      }
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    const fetchRefferedByUserDetails = async () => {
      if (referredByParam) {
        try {
          const response = await authServices.fetchUser(referredByParam);
          const userData = response?.data;
          const isBusiness = userData?.role_id === 4;
          if (isBusiness && referredByBusinessData) {
            const businesses = Array.isArray(referredByBusinessData)
              ? referredByBusinessData
              : [referredByBusinessData];

            if (businesses.length > 0) {
              const business = businesses[0];
              setUserData({
                name: business?.business || `${userData?.first_name} ${userData?.last_name}`,
                avatar: business?.businessImages?.[0]?.uri
                  ? parseImageUrl(business.businessImages[0].uri)
                  : `${awsImageRootUrl}/${userData?.avatar ?? ""}`,

                isBusiness: true
              });
            } else {
              setUserData({
                name: `${userData?.first_name} ${userData?.last_name}`,
                avatar: `${awsImageRootUrl}/${userData?.avatar}`,
                isBusiness: true
              });
            }
          } else if (!isBusiness) {
            setUserData({
              name: `${userData?.first_name} ${userData?.last_name}`,
              avatar: `${awsImageRootUrl}/${userData?.avatar}`,
              isBusiness: false
            });
          } else {
            setUserData({
              name: `${userData?.first_name} ${userData?.last_name}`,
              avatar: `${awsImageRootUrl}/${userData?.avatar}`,
              isBusiness: true
            });
          }
        } catch (error: any) {
          console.error("Error fetching referred user details:", error?.message);
        }
      }
    };

    fetchRefferedByUserDetails();
  }, [referredByParam, referredByBusinessData]);

  useEffect(() => {
    const fetchAvailabilities = async () => {
      try {
        setIsLoadingAvailabilities(true);
        await getMyItemsAvailabilities({
          businessId: Number(id),
          date: date.format("YYYY-MM-DD"),
        });
      } catch (error) {
        console.log("error fetching availabilities: ", error);
      } finally {
        setIsLoadingAvailabilities(false);
      }
    };
    fetchAvailabilities();
  }, [id, date, getMyItemsAvailabilities]);

  return (
    <PageContainer>
      {isEditing && businessDetails ? (
        <EditBusinessProfile
          setIsEditing={setIsEditing}
          businessDetails={businessDetails}
          refetch={refetch}
        />
      ) : (
        <Box sx={businessProfileStyles.mainContainer(theme)}>
          {isLoadingBusiness ? (
            <Box sx={businessProfileStyles.loadingContainer}>
              <CircularProgress size={60} />
            </Box>
          ) : (
            businessDetails && (
              <Box>
                {/* Business Image */}
                <Box sx={businessProfileStyles.imageContainer}>
                  {showBack === "true" && (
                    <img
                      src={ListIcon}
                      width={24}
                      height={24}
                      style={{
                        position: "absolute",
                        top: 20,
                        left: 20,
                        cursor: "pointer",
                      }}
                      alt=""
                      onClick={() => navigate(-1)}
                    />
                  )}
                  <img
                    src={
                      businessDetails?.businessImages[0]?.uri
                        ? parseImageUrl(businessDetails?.businessImages[0]?.uri)
                        : BusinessImage
                    }
                    alt="Business"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "0 0 1rem 1rem",
                    }}
                  />
                </Box>

                {/* Business Details */}
                {!showDetails ? (
                  <Box sx={businessProfileStyles.upperContainer}>
                    <Typography sx={[businessProfileStyles.upperBusinessNameText, { marginBottom: '0.25rem', },]}>
                      {businessDetails?.business}
                    </Typography>
                    <Box sx={businessProfileStyles.locationContainer}>
                      <img src={LocationIcon} width={12} height={12} alt="" />
                      <Typography sx={businessProfileStyles.locationText}>
                        {formatAddress(
                          businessDetails?.address1,
                          businessDetails?.city,
                          businessDetails?.state
                        )}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={businessProfileStyles.businessContainer}>
                    <Typography
                      sx={[
                        businessProfileStyles.mainHeading,
                        { textAlign: 'center', marginY: '0px' },
                      ]}
                    >
                      {businessDetails?.business}
                    </Typography>
                    <Box sx={businessProfileStyles.locationContainer}>
                      <img src={LocationIcon} width={12} height={12} alt="" />
                      <Typography sx={businessProfileStyles.locationText}>
                        {formatAddress(
                          businessDetails?.address1,
                          businessDetails?.city,
                          businessDetails?.state
                        )}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {businessDetails?.business_headline && (
                  <>
                    <Typography
                      sx={[
                        businessProfileStyles.headlineText,
                        { paddingTop: showDetails ? '0px' : '2px' },
                      ]}
                    >
                      {businessDetails?.business_headline}
                    </Typography>
                  </>
                )}

                <Box sx={{ marginX: "8px" }}>
                  <Box sx={{ marginX: "8px" }}>
                    {/* Buttons Container */}
                    {showDetails &&
                      (businessDetails?.business_users[0]?.id === user?.id ? (
                        <Button
                          variant="contained"
                          sx={businessProfileStyles.greenButton}
                          onClick={() => setIsEditing(true)}
                        >
                          <Edit sx={{ paddingRight: 1, fontSize: "20px" }} />
                          Edit Profile
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outlined"
                            sx={businessProfileStyles.greenButton}
                            onClick={onPressCreateChannel}
                          >
                            {loading ? (
                              <CircularProgress
                                sx={{ color: "white", paddingY: "2px" }}
                                size={24}
                              />
                            ) : (
                              <>
                                <img
                                  src={MessageIcon}
                                  alt="message-icon"
                                  style={{ paddingRight: "8px" }}
                                />
                                Message
                              </>
                            )}
                          </Button>
                          <Box sx={businessProfileStyles.buttonContainer}>
                            {/* btn1 */}
                            <Button
                              variant="outlined"
                              sx={businessProfileStyles.whiteButton}
                              onClick={handleOpenModal}
                            >
                              Connect Customer
                            </Button>
                            {/* btn2 */}
                            <Button
                              variant="outlined"
                              sx={businessProfileStyles.whiteButton}
                              onClick={handleBusinessShare}
                            >
                              Share Business
                            </Button>
                          </Box>
                        </>
                      ))}

                    {/* About */}
                    {businessDetails?.business_description && (
                      <>
                        <Divider
                          sx={{
                            bgcolor: "#E8E8EB3D",
                            marginY: "1.5rem",
                          }}
                        />
                        <Typography sx={businessProfileStyles.mainHeading}>
                          About
                        </Typography>
                        <CollapsibleText
                          limit={isDesktop ? 520 : 140}
                          text={businessDetails?.business_description}
                          seeMoreStyle={{
                            color: "#595959",
                            fontWeight: "400",
                            paddingRight: "0.5rem",
                          }}
                        />
                        <Divider
                          sx={{
                            bgcolor: "#E8E8EB3D",
                            marginY: "1.5rem",
                          }}
                        />
                      </>
                    )}

                    {/* Industries */}
                    {showDetails &&
                      businessDetails?.industries &&
                      businessDetails?.business_users[0]?.id === user?.id && (
                        <>
                          <Typography sx={businessProfileStyles.mainHeading}>
                            Industries
                          </Typography>
                          {businessDetails?.industries?.map((item, index) => (
                            <Chip
                              key={index}
                              label={item?.industry}
                              variant="outlined"
                              color="default"
                              sx={businessProfileStyles.chipStyles}
                            />
                          ))}
                        </>
                      )}

                    {/* Categories */}
                    {showDetails &&
                      businessDetails?.categories &&
                      businessDetails?.business_users[0]?.id === user?.id && (
                        <>
                          <Typography sx={businessProfileStyles.mainHeading}>
                            Categories
                          </Typography>
                          {businessDetails?.categories?.map((item, index) => (
                            <Chip
                              key={index}
                              label={item?.category}
                              variant="outlined"
                              color="default"
                              sx={businessProfileStyles.chipStyles}
                            />
                          ))}
                        </>
                      )}

                    {/* Contact Info */}
                    {showDetails && (
                      <Box>
                        <Typography sx={businessProfileStyles.mainHeading}>
                          Contact Info
                        </Typography>
                        <Box sx={businessProfileStyles.contactInfoContainer}>
                          <Box
                            sx={businessProfileStyles.contactInfoSingleContainer}
                          >
                            <Typography variant="body2">
                              <strong>Email:</strong>
                            </Typography>
                            <Typography sx={businessProfileStyles.contactTypo}>
                              {businessDetails?.business_users[0]?.email}
                            </Typography>
                          </Box>

                          <Box
                            sx={businessProfileStyles.contactInfoSingleContainer}
                          >
                            <Typography variant="body2">
                              <strong>Phone:</strong>
                            </Typography>
                            <Typography sx={businessProfileStyles.contactTypo}>
                              {businessDetails?.phone}
                            </Typography>
                          </Box>

                          <Box
                            sx={businessProfileStyles.contactInfoSingleContainer}
                          >
                            <Typography variant="body2">
                              <strong>Website:</strong>
                            </Typography>
                            <Typography
                              sx={businessProfileStyles.contactTypo}
                              component="a"
                              href={businessDetails?.address2}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {businessDetails?.address2}
                            </Typography>
                          </Box>

                          <Box
                            sx={businessProfileStyles.contactInfoSingleContainer}
                          >
                            <Typography variant="body2">
                              <strong>Address:</strong>
                            </Typography>
                            <Typography sx={businessProfileStyles.contactTypo}>
                              {[
                                businessDetails?.address1,
                                businessDetails?.city && businessDetails?.state ?
                                  `${businessDetails.city}, ${businessDetails.state}` :
                                  businessDetails?.city || businessDetails?.state
                              ].filter(Boolean).join(', ')}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Availabilities */}
                    {!showDetails && (
                      <Typography
                        sx={[
                          businessProfileStyles.mainHeading,
                          { marginBottom: '0.5rem' },
                        ]}
                      >
                        Book Now
                      </Typography>
                    )}
                  </Box>
                  <Availabilities
                    items={availabilitiesData?.items}
                    date={date}
                    setDate={setDate}
                    referredBy={referredByParam}
                    isLoadingAvailabilities={isLoadingAvailabilities}
                  />

                  {/* Bottom Section */}
                  <Box
                    sx={{
                      marginTop: {
                        xs: "25px",
                        sm: "50px",
                      },
                      marginBottom: {
                        xs: "25px",
                        sm: "25px",
                      },
                    }}
                  >
                    {userData && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: 6,
                        }}
                      >
                        <Typography
                          fontFamily={theme.typography.fontFamily}
                          sx={{
                            color: "#000",
                            fontSize: {
                              xs: "22px",
                              sm: "30px",
                            },
                            fontWeight: 600,
                          }}
                        >
                          Referred by {userData.isBusiness ? "Business" : ""}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "17px",
                            color: "#000",
                            display: "flex",
                            marginTop: {
                              xs: "12px",
                              sm: "15px",
                            },
                            alignItems: "center",
                            gap: "10px",
                            fontWeight: 400,
                            fontFamily: theme.typography.fontFamily,
                          }}
                        >
                          <img
                            src={userData?.avatar}
                            alt={`${userData.name} avatar`}
                            style={{
                              width: "33px",
                              height: "33px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                          {userData?.name}
                        </Typography>
                      </Box>
                    )}

                    <Divider
                      sx={{
                        bgcolor: "#D6D6DA",
                        margin: {
                          xs: "10px auto 25px auto",
                          sm: "20px auto 25px auto",
                        },
                        width: {
                          xs: "250px",
                          sm: "350px",
                        },
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "17px",
                          color: "#000",
                          fontWeight: 400,
                          fontFamily: theme.typography.fontFamily,
                          marginBottom: "2px",
                        }}
                      >
                        Powered by
                      </Typography>
                      <img src={ReferreLogo} width={122} alt="" />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )
          )}
        </Box>
      )}

      {businessDetails && (
        <ShareBusinessModal
          open={isShareModalOpen}
          close={() => setIsShareModalOpen(false)}
          link={link}
          businessName={businessDetails?.business}
        />
      )}

      {businessDetails?.business && (
        <CustomerModal
          open={isModalOpen}
          close={handleCloseModal}
          state={3}
          referral_type={10}
          user_id={Number(businessDetails?.business_users[0]?.id)}
          business_id={Number(businessDetails?.id)}
          businessName={businessDetails?.business}
        />
      )}
    </PageContainer>
  );
};

const BusinessProfile = () => {
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const queryParams = new URLSearchParams(location.search);
  const showDetails = queryParams.get("showDetails");

  if (user && showDetails) {
    return (
      <Layout>
        <BusinessProfilePage user={user} showDetails={showDetails} />
      </Layout>
    );
  } else {
    return <BusinessProfilePage user={user} />;
  }
};

export default BusinessProfile; 